import axios from 'axios';

export interface UserMetrics {
  promedioCalificaciones: number;
  tiempoTotalEstudio: number;
  ultimaActividad: string;
  progresoGeneral: number;
  totalIntentos: number;
}

export interface UserManagement {
  id: number;
  username: string;
  email: string;
  nombre: string;
  apellidos: string;
  telefono: string;
  avatarUrl?: string;
  activo: boolean;
  emailVerificado: boolean;
  telefonoVerificado: boolean;
  subscriptionStatus: string;
  creadoEn: string;
  actualizadoEn: string;
  roles: string[];
  metricas?: UserMetrics;

  // Detalles de suscripción
  subscriptionPlanName?: string;
  subscriptionEndDate?: string;
  daysRemaining?: number;
  autoRenew?: boolean;
  isCustomSubscription?: boolean;
}

export interface UserStatistics {
  totalUsuarios: number;
  sesionesActivas: number;
  nuevosEsteMes: number;
  distribucionPorPlan: Record<string, number>;
}

export interface UserFilters {
  page?: number;
  size?: number;
  search?: string;
  subscriptionStatus?: string;
  activo?: boolean;
  fechaDesde?: string;
  fechaHasta?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

class UserManagementService {
  async getUsers(filters: UserFilters = {}): Promise<PageResponse<UserManagement>> {
    const params = new URLSearchParams();
    
    if (filters.page !== undefined) params.append('page', filters.page.toString());
    if (filters.size !== undefined) params.append('size', filters.size.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.subscriptionStatus) params.append('subscriptionStatus', filters.subscriptionStatus);
    if (filters.activo !== undefined) params.append('activo', filters.activo.toString());
    if (filters.fechaDesde) params.append('fechaDesde', filters.fechaDesde);
    if (filters.fechaHasta) params.append('fechaHasta', filters.fechaHasta);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDir) params.append('sortDir', filters.sortDir);
    
    const response = await axios.get<PageResponse<UserManagement>>(
      `/api/admin/usuarios?${params.toString()}`
    );
    return response.data;
  }

  async getUserDetails(id: number): Promise<UserManagement> {
    const response = await axios.get<UserManagement>(`/api/admin/usuarios/${id}`);
    return response.data;
  }

  async updateUser(id: number, updates: Partial<UserManagement>): Promise<UserManagement> {
    const response = await axios.put<UserManagement>(`/api/admin/usuarios/${id}`, updates);
    return response.data;
  }

  async changeRole(id: number, rol: string): Promise<UserManagement> {
    const response = await axios.patch<UserManagement>(
      `/api/admin/usuarios/${id}/rol`,
      { rol }
    );
    return response.data;
  }

  async updateSubscription(id: number, subscriptionStatus: string): Promise<UserManagement> {
    const response = await axios.patch<UserManagement>(
      `/api/admin/usuarios/${id}/suscripcion`,
      { subscriptionStatus }
    );
    return response.data;
  }

  async toggleStatus(id: number, activo: boolean): Promise<UserManagement> {
    const response = await axios.patch<UserManagement>(
      `/api/admin/usuarios/${id}/estado`,
      { activo }
    );
    return response.data;
  }

  async deleteUser(id: number): Promise<void> {
    await axios.delete(`/api/admin/usuarios/${id}`);
  }

  async resetPassword(id: number): Promise<void> {
    await axios.post(`/api/admin/usuarios/${id}/reset-password`);
  }

  async getUserMetrics(id: number): Promise<UserMetrics> {
    const response = await axios.get<UserMetrics>(`/api/admin/usuarios/${id}/metricas`);
    return response.data;
  }

  async getSubscriptionPlans(): Promise<string[]> {
    const response = await axios.get<string[]>('/api/admin/usuarios/subscription-plans');
    return response.data;
  }

  async getUserStatistics(): Promise<UserStatistics> {
    const response = await axios.get<UserStatistics>('/api/dashboard/admin/users-stats');
    return response.data;
  }

  async grantFullAccess(id: number): Promise<void> {
    await axios.post(`/api/admin/usuarios/${id}/access`);
  }

  async revokeFullAccess(id: number): Promise<void> {
    await axios.delete(`/api/admin/usuarios/${id}/access`);
  }

  async getFullAccess(id: number): Promise<boolean> {
    const { data } = await axios.get<{ hasFullAccess: boolean }>(`/api/admin/usuarios/${id}/access`);
    return data.hasFullAccess;
  }
}

export const userManagementService = new UserManagementService();
